import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaGlobe, FaTimes, FaBars, FaUser } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom'; // Import Link
import SearchBar from './SearchBar';
import logo from './logo.png';
import AddListingModal from './AddListingModal'; // Import the modal component
import { useTranslation } from "react-i18next";

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #E1E0E0;
  font-family: 'Inter', sans-serif;
  top: 0;
  width: calc(100% - 60px);
  padding-left: 40px;
  z-index: 20;
  margin-top: ${props => (props.showNotification ? '50px' : '0')};  /* Adjust margin based on notification visibility */
`;

const Spacer = styled.div`
  height: 120px;
`;

const TopHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #007bff;
  display: flex;
  align-items: center;

  img {
    width: 80px;
    height: auto;
    margin-right: 8px;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  margin-left: 1rem;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  color: #333;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    margin-right: 8px;
    color: #007bff;
  }
`;

const Dropdown = styled.div`
  position: relative;
  display: inline-block;

  &:hover div {
    display: block;
  }
`;

const DropdownContent = styled.div`
  display: none;
  position: absolute;
  right: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  z-index: 1;

  a,
  button {
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    background: none;
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: left;

    &:hover {
      background-color: #f1f1f1;
    }
  }
`;

const DropdownContentLanguage = styled.div`
  display: none;
  position: absolute;
  right: 0;
  background-color: white;
  min-width: 40px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  z-index: 1;

  a,
  button {
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    background: none;
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: left;

    &:hover {
      background-color: #f3f3f3;
    }
  }
`;

const NotificationBar = styled.div`
  background-color: #ff4d4d;
  color: white;
  padding: 10px;
  width: 100%;
  text-align: center;
  position: fixed;  /* Make it fixed at the top of the screen */
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  align-items: left;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin-left: 50px;
  transition: color 0.2s ease;

  &:hover {
    color: #ddd;
  }
`;

const Header = ({ authenticated, setAuthenticated, language, setLanguage, userType }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState({ pending_reservations: 0, pending_bookings: 0 });
  const [showNotification, setShowNotification] = useState(false);
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  useEffect(() => {
  // Function to fetch pending reservations
  const fetchPendingReservations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/pending`, {
      // const response = await fetch('http://localhost:8000/pending', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`, // Ensure to send the JWT token
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingCount(data);
        if (data.pending_reservations > 0 || data.pending_bookings > 0) {
          setShowNotification(true);
        }
      } else {
        console.error('Failed to fetch pending reservations');
      }
    } catch (error) {
      console.error('Error fetching pending reservations:', error);
    }
  };

  // Initial fetch when the component mounts
  if (authenticated) {
    fetchPendingReservations();
  }

  // Set up the interval to call the fetch function every 1 minute
  const intervalId = setInterval(() => {
    if (authenticated) {
      fetchPendingReservations();
    }
  }, 60000); // 60000ms = 1 minute

  // Cleanup function to clear the interval when the component unmounts
  return () => clearInterval(intervalId);
}, [authenticated]); // Run this effect when the `authenticated` state changes


  const handleSignOut = () => {
    localStorage.removeItem('jwt_token');
    setAuthenticated(false);
    navigate('/');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleAddListing = () => {
    setIsModalOpen(true);
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleEN = () => {
    setLanguage('EN');
    i18n.changeLanguage("en");
  }
  
  const handleFI = () => {
    setLanguage('FI');
    i18n.changeLanguage("fi");
  }

  const handleWhy = () => {
    navigate('/why-room2w');
  };

  const handleEarn = () => {
    navigate('/earn-with-us');
  };

  return (
    <>
      {showNotification && (
        <NotificationBar>
          {t('pendingItems')} &nbsp;
          <Link to="/profile" style={{ color: 'white', textDecoration: 'underline' }}>
          {'  '}{t('clickHere')}
          </Link>
          <CloseButton onClick={handleCloseNotification}>
            <FaTimes />
          </CloseButton>
        </NotificationBar>
      )}
      <HeaderContainer showNotification={showNotification}>
        <TopHeader>
          <Link to='/'>
            <Logo>
              <img src={logo} alt="Logo" /> 
            </Logo>
          </Link>
          <NavRight>
            {!authenticated && (
              <NavButton onClick={handleSignIn}>
                {t('hostGuests')}
              </NavButton>
            )}
            {authenticated && (
              <>
                <NavButton onClick={handleAddListing}>
                  {t('addListing')}
                </NavButton>
              </>
            )}
            <Dropdown>
              <NavButton>
                  {t('aboutUs')}
              </NavButton>
              <DropdownContent>
                    <>
                      <button onClick={handleWhy}>{t('whyRoom2w')}?</button>
                      <button onClick={handleEarn}>{t('earnWithUs')}</button>
                    </>
                </DropdownContent>
            </Dropdown>
            <Dropdown>
              <NavButton>
                <FaGlobe />
              </NavButton>
              <DropdownContentLanguage>
                  <>
                    <button onClick={handleEN}>EN</button>
                    <button onClick={handleFI}>FI</button>
                  </>
              </DropdownContentLanguage>
            </Dropdown>

            <Dropdown>
              <NavButton>
                <FaBars />
                <FaUser />
              </NavButton>
              <DropdownContent>
                {authenticated ? (
                  <>
                    <button onClick={handleProfile}>{t('profile')}</button>
                    <button onClick={handleSignOut}>{t('signOut')}</button>
                  </>
                ) : (
                  <>
                    <a onClick={handleSignIn}>{t('signIn')}</a>
                    <a onClick={handleSignIn}>{t('signUp')}</a>
                  </>
                )}
              </DropdownContent>
            </Dropdown>
          </NavRight>
        </TopHeader>
        {/* <SearchBar /> */}
      </HeaderContainer>

      {isModalOpen && <AddListingModal closeModal={() => setIsModalOpen(false)} />}
    </>
  );
};

export default Header;
